import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { FileXls, CheckCircle, WarningCircle, ArrowLeft, Users } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import authedFetch from '../lib/authedFetch';

// Care DS v3: esta tela usa o kit do CareOS (src/styles/care-ui.css) — .ui-pagina,
// .ui-tela, .ui-card, .ui-titulo, .ui-kpi, .ui-linhas, .ui-aviso, .ui-botao, .skel —
// e papéis --care-*/shadcn para o que o kit não cobre. Ícones Phosphor em currentColor.

// Overlay de hover/pressed POR CIMA do fundo sunken (não troca o fundo nem o matiz)
const ZONA_OVERLAY =
  'hover:[background-image:linear-gradient(var(--care-state-hover-overlay),var(--care-state-hover-overlay))] ' +
  'active:[background-image:linear-gradient(var(--care-state-pressed-overlay),var(--care-state-pressed-overlay))]';

// Entrada só do card que aparece por ação do usuário (motion orienta, não decora)
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const cardAnim = () => {
  if (prefersReducedMotion()) {
    return { initial: false, animate: { opacity: 1, y: 0 }, transition: { duration: 0 } };
  }
  return {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.28, ease: [0.2, 0, 0, 1] },
  };
};

// Lê a resposta sem quebrar quando o corpo não é JSON (413/502 com HTML de proxy)
const lerResposta = async (response) => {
  const texto = await response.text();
  try {
    return texto ? JSON.parse(texto) : null;
  } catch {
    return null;
  }
};

const mensagemDeErro = (data) =>
  data && typeof data.detail === 'string' && data.detail
    ? data.detail
    : 'Não foi possível processar a planilha.';

// Caixa de upload — definida FORA do componente pai pra não remontar a cada render
// (antes era recriada dentro do UploadPatrimonio, perdendo identidade no reconciler)
const UploadBox = ({ id, title, description, icon: Icon, onUpload, inputRef, uploading, result, accept = '.xlsx,.xls' }) => {
  const openPicker = () => {
    if (!uploading) inputRef.current?.click();
  };
  const resultadoId = `upload-${id}-resultado`;
  const comErro = Boolean(result && !result.success);

  let estadoZona;
  if (uploading) {
    estadoZona = 'border-[color:var(--care-border-default)] cursor-progress';
  } else if (comErro) {
    estadoZona = `border-[color:var(--destructive)] cursor-pointer ${ZONA_OVERLAY}`;
  } else {
    estadoZona = `border-[color:var(--care-border-interactive)] cursor-pointer hover:border-[color:var(--care-border-strong)] ${ZONA_OVERLAY}`;
  }

  return (
  <section className="ui-card" aria-labelledby={`upload-${id}-titulo`}>
    <div className="flex items-start gap-3">
      <span className="flex-none mt-0.5 text-[color:var(--muted-foreground)]" aria-hidden="true">
        <Icon size={20} weight="regular" />
      </span>
      <div className="ui-pilha min-w-0" data-gap="1">
        <h2 id={`upload-${id}-titulo`} className="ui-titulo">{title}</h2>
        <p className="ui-paragrafo">{description}</p>
      </div>
    </div>

    <div
      role="button"
      tabIndex={uploading ? -1 : 0}
      aria-busy={uploading ? 'true' : undefined}
      aria-describedby={result ? resultadoId : undefined}
      aria-label={`Selecionar arquivo: ${title}`}
      onClick={openPicker}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openPicker();
        }
      }}
      className={`
        min-h-[96px] flex items-center justify-center
        border border-dashed rounded-lg p-4 sm:p-6 text-center
        bg-[var(--care-bg-surface-sunken)] text-[color:var(--foreground)]
        transition-colors
        ${estadoZona}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={(e) => e.target.files && e.target.files.length > 0 && onUpload(e.target.files[0])}
        className="hidden"
        tabIndex={-1}
      />

      {uploading ? (
        <div className="flex flex-col items-center gap-2 w-full" aria-hidden="true">
          <span className="skel block w-8 h-8" aria-hidden="true" />
          <span className="skel block h-4 w-40 max-w-full" aria-hidden="true" />
          <p className="ui-paragrafo">Processando…</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <span className="text-[color:var(--muted-foreground)]" aria-hidden="true">
            <FileXls size={24} weight="regular" />
          </span>
          <p className="text-[length:var(--care-text-base)] font-medium text-[color:var(--foreground)]">
            Clique para selecionar
          </p>
          <p className="ui-paragrafo">
            .xlsx ou .xls
          </p>
        </div>
      )}
    </div>

    <p className="sr-only" role="status" aria-live="polite">
      {uploading ? `Processando ${title}…` : ''}
    </p>

    {result && (
      <div
        id={resultadoId}
        className="ui-aviso"
        data-tom={result.success ? 'positivo' : 'negativo'}
        role={result.success ? 'status' : 'alert'}
      >
        {result.success ? (
          <CheckCircle size={20} weight="regular" aria-hidden="true" />
        ) : (
          <WarningCircle size={20} weight="regular" aria-hidden="true" />
        )}
        <div className="ui-pilha min-w-0" data-gap="1">
          <p>
            <span className="sr-only">{result.success ? 'Sucesso: ' : 'Erro: '}</span>
            {result.message}
          </p>
          {result.success && result.data && (
            <p className="ui-meta font-normal">
              {result.formato && `${result.formato} · `}
              Data: {new Date(result.data + 'T00:00:00').toLocaleDateString('pt-BR')}
              {result.clientes && ` · ${result.clientes} clientes`}
              {result.produtos && ` · ${result.produtos} produtos`}
            </p>
          )}
          {result.success && result.abas && result.abas.length > 0 && (
            <p className="ui-meta font-normal">
              Abas: {result.abas.join(', ')}
            </p>
          )}
        </div>
      </div>
    )}
  </section>
  );
};

const UploadPatrimonio = () => {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState({ btg: false, xp: false, crm: false });
  const [uploadResults, setUploadResults] = useState({ btg: null, xp: null, crm: null });
  const [estatisticas, setEstatisticas] = useState(null);
  // null = carregando (esqueleto); [] = vazio
  const [historicoUploads, setHistoricoUploads] = useState(null);
  const [erroEstatisticas, setErroEstatisticas] = useState(false);
  const [erroHistorico, setErroHistorico] = useState(false);

  const btgInputRef = useRef(null);
  const xpInputRef = useRef(null);
  const crmInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Carregar estatísticas e histórico ao montar
  useEffect(() => {
    carregarEstatisticas();
    carregarHistorico();
  }, []);

  // Recarregar estatísticas e histórico após uploads bem-sucedidos
  useEffect(() => {
    if (uploadResults.btg?.success || uploadResults.xp?.success || uploadResults.crm?.success) {
      setTimeout(() => {
        carregarEstatisticas();
        carregarHistorico();
      }, 1000);
    }
  }, [uploadResults]);

  const carregarEstatisticas = async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/patrimonio/estatisticas`);
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setEstatisticas(await res.json());
      setErroEstatisticas(false);
    } catch (err) {
      // Antes falhava em silêncio e a seção simplesmente sumia
      console.error('Erro ao carregar estatísticas:', err);
      setErroEstatisticas(true);
    }
  };

  const carregarHistorico = async () => {
    try {
      const res = await authedFetch(`${API_URL}/api/patrimonio/historico-uploads?limit=10`);
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setHistoricoUploads(data.uploads || []);
      setErroHistorico(false);
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
      setErroHistorico(true);
    }
  };

  const handleUploadBTG = async (file) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setUploadResults(prev => ({
        ...prev,
        btg: { success: false, message: 'Arquivo deve ser Excel (.xlsx ou .xls)' }
      }));
      return;
    }

    setUploading(prev => ({ ...prev, btg: true }));
    setUploadResults(prev => ({ ...prev, btg: null }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authedFetch(`${API_URL}/api/patrimonio/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await lerResposta(response);

      if (response.ok && data) {
        setUploadResults(prev => ({
          ...prev,
          btg: {
            success: true,
            message: data.message,
            data: data.data_relatorio,
            clientes: data.clientes_processados,
            produtos: data.produtos_processados,
            formato: data.formato_detectado
          }
        }));
      } else {
        setUploadResults(prev => ({
          ...prev,
          btg: { success: false, message: mensagemDeErro(data) }
        }));
      }
    } catch (error) {
      setUploadResults(prev => ({
        ...prev,
        btg: { success: false, message: 'Erro de conexão com o servidor' }
      }));
    } finally {
      setUploading(prev => ({ ...prev, btg: false }));
      if (btgInputRef.current) {
        btgInputRef.current.value = '';
      }
    }
  };

  const handleUploadXP = async (file) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setUploadResults(prev => ({
        ...prev,
        xp: { success: false, message: 'Arquivo deve ser Excel (.xlsx ou .xls)' }
      }));
      return;
    }

    setUploading(prev => ({ ...prev, xp: true }));
    setUploadResults(prev => ({ ...prev, xp: null }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authedFetch(`${API_URL}/api/patrimonio/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await lerResposta(response);

      if (response.ok && data) {
        setUploadResults(prev => ({
          ...prev,
          xp: {
            success: true,
            message: data.message,
            data: data.data_relatorio,
            clientes: data.clientes_processados,
            produtos: data.produtos_processados,
            formato: data.formato_detectado,
            abas: data.abas_processadas
          }
        }));
      } else {
        setUploadResults(prev => ({
          ...prev,
          xp: { success: false, message: mensagemDeErro(data) }
        }));
      }
    } catch (error) {
      setUploadResults(prev => ({
        ...prev,
        xp: { success: false, message: 'Erro de conexão com o servidor' }
      }));
    } finally {
      setUploading(prev => ({ ...prev, xp: false }));
      if (xpInputRef.current) {
        xpInputRef.current.value = '';
      }
    }
  };

  const handleUploadCRM = async (file) => {
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      setUploadResults(prev => ({
        ...prev,
        crm: { success: false, message: 'Arquivo deve ser Excel (.xlsx ou .xls)' }
      }));
      return;
    }

    setUploading(prev => ({ ...prev, crm: true }));
    setUploadResults(prev => ({ ...prev, crm: null }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await authedFetch(`${API_URL}/api/patrimonio/upload-crm`, {
        method: 'POST',
        body: formData,
      });

      const data = await lerResposta(response);

      if (response.ok && data) {
        setUploadResults(prev => ({
          ...prev,
          crm: {
            success: true,
            message: data.message,
            clientes: data.clientes_atualizados
          }
        }));
      } else {
        setUploadResults(prev => ({
          ...prev,
          crm: { success: false, message: mensagemDeErro(data) }
        }));
      }
    } catch (error) {
      setUploadResults(prev => ({
        ...prev,
        crm: { success: false, message: 'Erro de conexão com o servidor' }
      }));
    } finally {
      setUploading(prev => ({ ...prev, crm: false }));
      if (crmInputRef.current) {
        crmInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="ui-pagina">
      <button
        type="button"
        className="ui-botao min-h-[44px] px-3 -ml-3 mb-2"
        data-variante="fantasma"
        onClick={() => navigate('/dashboard')}
      >
        <ArrowLeft size={16} weight="bold" aria-hidden="true" />
        Voltar ao Dashboard
      </button>

      <header className="ui-tela">
        <div className="ui-tela__texto">
          <h1 className="ui-tela__titulo">Upload de Planilhas</h1>
          <p className="ui-tela__sub">
            Faça upload das planilhas BTG, XP e CRM para processar os dados de patrimônio
          </p>
        </div>
      </header>

      <div className="ui-pilha" data-gap="7">
        {/* Instruções */}
        <section className="ui-card">
          <h2 className="ui-rotulo">Ordem Recomendada de Upload:</h2>
          <ol className="ui-lista list-decimal">
            <li><strong>CRM XP</strong> (opcional mas recomendado) - Para atualizar nomes dos clientes da XP</li>
            <li><strong>Planilha BTG</strong> - Arquivo consolidado com 1 aba "Export"</li>
            <li><strong>Planilha XP</strong> - Arquivo com múltiplas abas (Ações, Fundos, etc.)</li>
          </ol>
          <p className="ui-paragrafo">
            Se carregar o CRM primeiro, os nomes dos clientes XP serão preenchidos automaticamente
          </p>
        </section>

        {/* Aviso discreto quando estatísticas/histórico não carregam */}
        {(erroEstatisticas || erroHistorico) && (
          <p className="ui-aviso" data-tom="atencao" role="status">
            <WarningCircle size={16} weight="bold" aria-hidden="true" />
            <span>
              Não foi possível carregar {erroEstatisticas && erroHistorico
                ? 'as estatísticas e o histórico de uploads'
                : erroEstatisticas ? 'as estatísticas' : 'o histórico de uploads'}. Os uploads continuam funcionando.
            </span>
          </p>
        )}

        {/* Estatísticas: carregando (skeleton na forma final) */}
        {estatisticas === null && !erroEstatisticas && (
          <section className="ui-card" role="status" aria-busy="true" aria-label="Carregando dados">
            <span className="skel block h-5 w-40" aria-hidden="true" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" aria-hidden="true">
              {[0, 1, 2, 3].map((n) => (
                <div key={n} className="ui-pilha" data-gap="1">
                  <span className="skel block h-8 w-16" />
                  <span className="skel block h-3 w-12" />
                </div>
              ))}
            </div>
            <span className="skel block h-3 w-24" aria-hidden="true" />
          </section>
        )}

        {/* Estatísticas: vazio com voz editorial */}
        {estatisticas && !(estatisticas.total_clientes > 0) && (
          <section className="ui-card">
            <h2 className="ui-titulo">Dados Carregados</h2>
            <p className="ui-vazio">
              Nenhuma planilha carregada ainda. Comece pelo CRM XP, logo abaixo.
            </p>
          </section>
        )}

        {/* Estatísticas Atuais */}
        {estatisticas && estatisticas.total_clientes > 0 && (
          <section className="ui-card">
            <h2 className="ui-titulo">Dados Carregados</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'BTG', value: estatisticas.clientes_btg },
                { label: 'XP', value: estatisticas.clientes_xp },
                { label: 'Total', value: estatisticas.total_clientes },
                { label: 'Produtos', value: estatisticas.total_produtos },
              ].map((kpi) => (
                <div key={kpi.label} className="ui-kpi" data-tamanho="lg">
                  <span className="ui-kpi__valor">
                    {kpi.value != null ? Number(kpi.value).toLocaleString('pt-BR') : '—'}
                  </span>
                  <span className="ui-kpi__rotulo">{kpi.label}</span>
                </div>
              ))}
            </div>
            <p className="ui-meta">
              Data: {new Date(estatisticas.data_relatorio + 'T00:00:00').toLocaleDateString('pt-BR')}
            </p>
          </section>
        )}

        {/* Histórico de Uploads: carregando (esqueleto na forma da lista) */}
        {historicoUploads === null && !erroHistorico && (
          <section className="ui-card" role="status" aria-busy="true" aria-label="Carregando últimos uploads">
            <h2 className="ui-titulo">Últimos Uploads</h2>
            <div className="ui-linhas ui-carregando" aria-hidden="true">
              {[undefined, '1', '2'].map((i, n) => (
                <div key={n} className="ui-linha" data-i={i}>
                  <span className="skel ui-carregando__nome" />
                  <span className="skel ui-carregando__meta" />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Histórico de Uploads */}
        {Array.isArray(historicoUploads) && (
          <section className="ui-card">
            <h2 className="ui-titulo">Últimos Uploads</h2>
            {historicoUploads.length === 0 ? (
              <p className="ui-vazio">Nenhum upload registrado ainda.</p>
            ) : (
              <div className="ui-linhas">
                {historicoUploads.slice(0, 5).map((upload, idx) => (
                  <div key={upload.upload_timestamp || idx} className="ui-linha flex-wrap py-2">
                    <span className="ui-pilula" data-tom="forte">{upload.tipo}</span>
                    <span className="ui-linha__nome" title={upload.filename}>{upload.filename}</span>
                    <span className="ui-linha__fim ui-empurra ui-meta">
                      {upload.clientes_processados} clientes ·{' '}
                      {new Date(upload.upload_timestamp).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Upload CRM XP */}
        <UploadBox
          id="crm"
          title="CRM XP"
          description="Arquivo com Conta e Nome dos clientes XP"
          icon={Users}
          onUpload={handleUploadCRM}
          inputRef={crmInputRef}
          uploading={uploading.crm}
          result={uploadResults.crm}
        />

        {/* Upload BTG */}
        <UploadBox
          id="btg"
          title="Planilha BTG"
          description="Formato consolidado - 1 aba 'Export'"
          icon={FileXls}
          onUpload={handleUploadBTG}
          inputRef={btgInputRef}
          uploading={uploading.btg}
          result={uploadResults.btg}
        />

        {/* Upload XP */}
        <UploadBox
          id="xp"
          title="Planilha XP"
          description="Formato multi-abas (Ações, Fundos, Renda Fixa, etc.)"
          icon={FileXls}
          onUpload={handleUploadXP}
          inputRef={xpInputRef}
          uploading={uploading.xp}
          result={uploadResults.xp}
        />

        {/* Botões de Navegação */}
        {(uploadResults.btg?.success || uploadResults.xp?.success) && (
          <motion.section {...cardAnim()} className="ui-card">
            <h2 className="ui-titulo flex items-center gap-2">
              <span className="ui-sinal" data-estado="ok">
                <CheckCircle size={16} weight="bold" aria-hidden="true" />
              </span>
              Dados processados com sucesso!
            </h2>
            <p className="ui-paragrafo">
              Agora você pode visualizar os dados nas páginas:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                className="ui-botao flex-1 min-h-[44px]"
                data-variante="solido"
                onClick={() => navigate('/acompanhamento')}
              >
                Acompanhamento Semanal
              </button>
              <button
                type="button"
                className="ui-botao flex-1 min-h-[44px]"
                data-variante="fio"
                onClick={() => navigate('/produtos')}
              >
                Clientes e Produtos
              </button>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
};

export default UploadPatrimonio;
